import { Injectable, computed, inject, signal } from '@angular/core';
import { take, timeout } from 'rxjs';
import {
  AccountHttpService,
  CapabilitiesDto,
} from '@core/services/api-client.generated';
import {
  AUTHORITY_PROBLEM_CODES,
  toAuthorityProblem,
} from './application-authority-problem';

/** Longer than the API's 5 s authority deadline plus a retry-free round trip. */
export const CAPABILITIES_READ_DEADLINE_MS = 10_000;
import type { ApplicationCapabilityName } from './application-capability';

export {
  APPLICATION_CAPABILITIES,
  CAPABILITIES_PATH,
  type ApplicationCapabilityName,
} from './application-capability';

export interface CapabilityDecision {
  allowed: boolean;
  /** Safe reason code about the caller's own access, e.g. `allowed`, `role-not-held`. */
  reason: string;
}

export type ApplicationCapabilitiesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'loaded';
      policyVersion: string;
      decisions: Partial<Record<string, CapabilityDecision>>;
    }
  /** 404: PM application authority is not enforced here, so these capabilities do not exist. */
  | { status: 'not-enforced' }
  /** 403 with the API's problem code, e.g. `impersonation-not-allowed`. */
  | { status: 'refused'; code: string | null }
  /** 503 `authority-unavailable`: authority could not be established right now. Retry. */
  | { status: 'unavailable' }
  | { status: 'error' };

/**
 * The caller's own PM-decided capabilities, for **explaining** the UI only. The API decides every
 * request from current PM authority; a stale value here can never grant anything. Refresh on entry
 * to a screen that depends on it and after any 403 from a PM-decided endpoint.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationCapabilitiesService {
  private readonly _api = inject(AccountHttpService);
  private readonly _state = signal<ApplicationCapabilitiesState>({
    status: 'idle',
  });
  private _generation = 0;
  private _inFlight = false;
  private _waiters: {
    generation: number;
    resolve: (state: ApplicationCapabilitiesState) => void;
  }[] = [];

  readonly state = this._state.asReadonly();

  /** True only when the deployment enforces PM authority and the capabilities were read. */
  readonly enforced = computed(() => this._state().status === 'loaded');

  allows(capability: ApplicationCapabilityName): boolean {
    const state = this._state();
    return (
      state.status === 'loaded' && state.decisions[capability]?.allowed === true
    );
  }

  decision(capability: ApplicationCapabilityName): CapabilityDecision | null {
    const state = this._state();
    return state.status === 'loaded'
      ? (state.decisions[capability] ?? null)
      : null;
  }

  /**
   * Re-read the caller's capabilities. Resolves with the state that this read, or a newer one,
   * produced, so a route guard can act on a fresh answer rather than on a remembered one.
   */
  refresh(): Promise<ApplicationCapabilitiesState> {
    // Only the newest request may write the state, so a slow earlier answer cannot overwrite it.
    const generation = ++this._generation;
    const settled = new Promise<ApplicationCapabilitiesState>((resolve) =>
      this._waiters.push({ generation, resolve })
    );
    // Revalidate without flicker: a loaded state stays visible until the new answer replaces it
    // (the API enforces every request, so a briefly stale explanation grants nothing).
    if (this._state().status !== 'loaded') {
      this._state.set({ status: 'loading' });
    }
    this._inFlight = true;
    this._api
      .getCapabilities()
      // A hung read must not hold a route guard open: after the deadline it is an error.
      .pipe(timeout(CAPABILITIES_READ_DEADLINE_MS), take(1))
      .subscribe({
        next: (capabilities) => this._settle(generation, toLoaded(capabilities)),
        error: (error: unknown) => {
          const problem = toAuthorityProblem(error);
          // Only the API's own coded answers mean "PM authority decides here". An uncoded 403 or
          // 503 (an ingress, a rollout, a gateway) says nothing about the mode, so it is an error,
          // which keeps the pre-M2a claim fallback where authority is not enforced.
          this._settle(
            generation,
            problem.status === 404
              ? { status: 'not-enforced' }
              : problem.status === 403 && problem.code
                ? { status: 'refused', code: problem.code }
                : problem.status === 503 &&
                    problem.code === AUTHORITY_PROBLEM_CODES.authorityUnavailable
                  ? { status: 'unavailable' }
                  : { status: 'error' }
          );
        },
      });
    return settled;
  }

  /**
   * A protected request was refused (403). Where PM authority is known to decide (the last read
   * succeeded, was refused or found authority unavailable), re-read so menus and pages stop
   * offering what the API now denies. Never where authority is not enforced (a 404), so the
   * deployed default makes no extra request, and never while a read is already running.
   */
  refreshAfterDenial(): void {
    const status = this._state().status;
    if (
      !this._inFlight &&
      (status === 'loaded' || status === 'refused' || status === 'unavailable')
    ) {
      void this.refresh();
    }
  }

  /** Forget the caller's capabilities (sign-out). Any pending read's answer is ignored. */
  reset(): void {
    const generation = ++this._generation;
    this._inFlight = false;
    this._state.set({ status: 'idle' });
    this._release(generation);
  }

  private _settle(generation: number, state: ApplicationCapabilitiesState) {
    if (generation !== this._generation) {
      return;
    }
    this._inFlight = false;
    this._state.set(state);
    this._release(generation);
  }

  private _release(generation: number) {
    const state = this._state();
    const ready = this._waiters.filter((waiter) => waiter.generation <= generation);
    this._waiters = this._waiters.filter((waiter) => waiter.generation > generation);
    ready.forEach((waiter) => waiter.resolve(state));
  }
}

function toLoaded(dto: CapabilitiesDto): ApplicationCapabilitiesState {
  const decisions: Partial<Record<string, CapabilityDecision>> = {};
  for (const capability of dto.capabilities ?? []) {
    decisions[capability.capability] = {
      allowed: capability.allowed === true,
      reason: capability.reason,
    };
  }
  return { status: 'loaded', policyVersion: dto.policyVersion, decisions };
}
